/**
 * E06-F01-T07 — Verify-strictness toggle + custom-entry pills (company type /
 * funding) section of the Intake form.
 *
 *   - verifyStrictness toggle (strict | lenient) with an explanation
 *     (verifyHint): strict = PASS needs explicit relevance confirmation
 *     ("used in high-tech" / "may work with" ≠ PASS — UCT rule); lenient =
 *     "may work with" OK. The selected value is written into
 *     criteria.verifyStrictness.
 *   - company-type + funding multi-select pill groups (PillMultiSelect) whose
 *     "+custom…" input adds a custom pill (active/selected by default — addCustomPill).
 *     Re-clicking a pill toggles it. The type list includes non-IT donors
 *     (Manufacturer/Plant, Enterprise/Corporation, Distributor, …).
 *
 * Controlled — values flow from the parent criteria and out via the callbacks.
 * Pure vocab/rules live in intakeModel.ts.
 */
import type { JSX } from 'react';
import { SegmentedToggle } from '../ui/SegmentedToggle.js';
import { PillMultiSelect } from '../ui/PillMultiSelect.js';
import type { VerifyStrictness } from '@eer/shared';
import {
  COMPANY_TYPE_PRESETS,
  FUNDING_PRESETS,
  VERIFY_OPTIONS,
  verifyHint,
} from './intakeModel.js';

export interface VerifyStrictnessSectionProps {
  readonly verifyStrictness: VerifyStrictness;
  readonly onVerifyStrictnessChange: (v: VerifyStrictness) => void;
  readonly companyTypes: readonly string[];
  readonly onCompanyTypesChange: (next: string[]) => void;
  readonly fundingStages: readonly string[];
  readonly onFundingStagesChange: (next: string[]) => void;
}

export function VerifyStrictnessSection({
  verifyStrictness,
  onVerifyStrictnessChange,
  companyTypes,
  onCompanyTypesChange,
  fundingStages,
  onFundingStagesChange,
}: VerifyStrictnessSectionProps): JSX.Element {
  return (
    <section data-testid="intake-verify-types" className="flex flex-col gap-4">
      {/* Verify strictness */}
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-[var(--color-text-strong)]">Verification</span>
        <SegmentedToggle<VerifyStrictness>
          options={VERIFY_OPTIONS}
          value={verifyStrictness}
          onChange={onVerifyStrictnessChange}
          ariaLabel="Verification strictness"
          testId="intake-verify"
        />
        <p data-testid="verify-hint" className="text-xs text-[var(--color-text-muted)]">
          {verifyHint(verifyStrictness)}
        </p>
      </div>

      {/* Company type pills + custom */}
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-[var(--color-text-strong)]">Company type</span>
        <PillMultiSelect
          presets={COMPANY_TYPE_PRESETS}
          selected={companyTypes}
          onChange={onCompanyTypesChange}
          ariaLabel="Company type"
          customPlaceholder="+custom type…"
          testId="intake-company-types"
        />
      </div>

      {/* Funding pills + custom */}
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-[var(--color-text-strong)]">Funding</span>
        <PillMultiSelect
          presets={FUNDING_PRESETS}
          selected={fundingStages}
          onChange={onFundingStagesChange}
          ariaLabel="Funding stage"
          customPlaceholder="+custom funding…"
          testId="intake-funding"
        />
      </div>
    </section>
  );
}
